import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";

import { Feedback, SearchFeedbacksFilter } from "./Feedback";
import {
  defaultPageSize,
  defaultPaginationOptions,
  emptyPaginationDetails,
  getPaginationFirst,
  getPaginationOptionsFromDataTable,
} from "../../common/api/paginationHelper";
import { searchFeedbacks } from "./feedbackApi";
import { LayoutContext } from "../../common/components/layout/LayoutContext";
import { fixedWidthColumnStyle, LS_SELECTED_DEPARTMENT_ID } from "../../common/utils/common";
import { routes } from "../../common/routing/routes";
import { UserRoleEnum } from "../userRoles/UserRole";
import { Privilege } from "../auth/Privilege";
import { canAccess } from "../auth/utils";
import { selectAuthUser, selectProfile } from "../auth/authSlice";
import NoResultsFound from "../../common/components/noResultsFound/NoResultsFound";
import FeedbackAnswersDropdown from "./FeedbackAnswersDropdown";
import FeedbackAnswerRating from "./FeedbackAnswerRating";
import UsersDropdown from "../users/UsersDropdown";
import DepartmentsDropdownCard from "../departments/DepartmentsDropdownCard";
import ProtectedChildren from "../../common/routing/ProtectedChildren";

const defaultFilters = {
  ticketId: { value: null },
  answerValue: { value: null },
  agentId: { value: null },
};

export default function FeedbacksPage() {
  const { setFullContentWidth, setStandardContentWidth } = useContext(LayoutContext);
  const navigate = useNavigate();
  const authUser = useSelector(selectAuthUser);
  const profile = useSelector(selectProfile);
  const isAdmin = canAccess(Privilege.Admin, authUser.privilege);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(localStorage.getItem(LS_SELECTED_DEPARTMENT_ID) || profile.departmentId);
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [filters, setFilters] = useState<any>(defaultFilters);
  const [pagination, setPagination] = useState(emptyPaginationDetails);
  const [first, setFirst] = useState(() => getPaginationFirst(pagination.pageNumber, defaultPageSize));
  const [rows, setRows] = useState(defaultPageSize);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setFullContentWidth();
    return () => setStandardContentWidth();
  }, []);

  useEffect(() => {
    fetchFeedbacks();
  }, [first, rows, filters, selectedDepartmentId]);

  const fetchFeedbacks = async () => {
    const payload: SearchFeedbacksFilter = {
      departmentId: selectedDepartmentId,
      ticketId: filters.ticketId.value,
      answerValue: filters.answerValue.value,
      agentId: isAdmin ? filters.agentId.value : authUser.id,
      sortParameters: [
        {
          columnName: "Created",
          isDescending: true,
        },
      ],
      ...getPaginationOptionsFromDataTable(first, rows),
    };
    setIsLoading(true);
    const feedbacksResponse = await searchFeedbacks(payload);
    setFeedbacks(feedbacksResponse.feedbacks);
    setPagination(feedbacksResponse.pagination);
    setIsLoading(false);
  };

  const resetPagination = () => {
    setPagination(emptyPaginationDetails);
    setFirst(getPaginationFirst(emptyPaginationDetails.pageNumber, defaultPageSize));
    setRows(defaultPageSize);
  };

  const copyFeedbackUrl = (feedbackId: string) => {
    navigator.clipboard.writeText(`${window.location.origin}${routes.feedback.navigatePath}/${feedbackId}`);
    toast.success("Link-ul catre survey a fost copiat in clipboard!");
  };

  return (
    <div className="flex flex-column gap-2">
      <ProtectedChildren requiredPrivilege={Privilege.Admin}>
        <DepartmentsDropdownCard
          className="mb-4"
          showAll
          value={selectedDepartmentId}
          onChange={(department) => setSelectedDepartmentId(department)}
        ></DepartmentsDropdownCard>
      </ProtectedChildren>
      <Card title="Surveys">
        <DataTable
          pt={{
            column: {
              headerFilterClearButton: {
                className: "display-none",
              },
            },
          }}
          rowClassName={() => "cursor-pointer"}
          size="small"
          value={feedbacks}
          lazy
          paginator
          first={first}
          rows={rows}
          rowsPerPageOptions={defaultPaginationOptions}
          totalRecords={pagination.totalCount}
          filterDisplay="row"
          filters={filters}
          filterDelay={500}
          emptyMessage={<NoResultsFound />}
          loading={isLoading}
          onPage={(e) => {
            setFirst(e.first);
            setRows(e.rows);
          }}
          onFilter={(e) => {
            setFilters(e.filters);
            resetPagination();
          }}
          onRowClick={(e) => {
            navigate(`${routes.agentTicketDetails.navigatePath}/${e.data.ticketId}`);
          }}
        >
          <Column
            style={fixedWidthColumnStyle(100)}
            className="text-center"
            header="Tichet Id"
            field="ticketId"
            filter
            showFilterMatchModes={false}
            showFilterMenu={false}
            body={(rowData: Feedback) => <span>{rowData.prettyTicketId}</span>}
          ></Column>
          <Column
            style={fixedWidthColumnStyle(150)}
            header="Agent"
            field="agentId"
            filter={isAdmin}
            showFilterMatchModes={false}
            showFilterMenu={false}
            filterElement={(options: ColumnFilterElementTemplateOptions) => (
              <UsersDropdown showClear role={UserRoleEnum.Agent} value={options.value} onChange={(e) => options.filterApplyCallback(e)} />
            )}
            body={(rowData: Feedback) => <span>{rowData.agentName}</span>}
          ></Column>
          <Column
            style={fixedWidthColumnStyle(180)}
            header="Nota"
            field="answerValue"
            filter
            showFilterMatchModes={false}
            showFilterMenu={false}
            filterElement={(options: ColumnFilterElementTemplateOptions) => (
              <FeedbackAnswersDropdown showClear value={options.value} onChange={(e) => options.filterApplyCallback(e)} />
            )}
            body={(rowData: Feedback) =>
              rowData.answer ? <FeedbackAnswerRating value={rowData.answer.value} readOnly /> : <span>Fara nota</span>
            }
          ></Column>
          <Column style={fixedWidthColumnStyle(100)} header="Apreciere" field="answer.label"></Column>
          <Column header="Descriere" field="answer.description"></Column>
          <Column style={fixedWidthColumnStyle(150)} header="Creat de" field="userName"></Column>
          <Column style={fixedWidthColumnStyle(140)} header="Data crearii" field="createdPretty"></Column>
          <Column
            style={fixedWidthColumnStyle(60)}
            body={(rowData: Feedback) => <Button icon="pi pi-copy" rounded text onClick={() => copyFeedbackUrl(rowData.id)} />}
          ></Column>
        </DataTable>
      </Card>
    </div>
  );
}
